// WO-33 — Piece 2: the getting-started checklist (spec §3).
// Desktop: a 320 px panel fixed bottom-right that minimises to a pill.
// Mobile: the pill sits above the bottom edge and opens as a bottom sheet.
// Tasks tick themselves from the server (R4); nobody ticks by hand.
import React, { useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useOnboarding } from './OnboardingContext';
import { onboardingCopy } from './copy';
import { panel, fontStyle } from './styles';

function taskText(task, plan) {
  const t = onboardingCopy.en.checklist.tasks;
  if (task.id === 'team') return t.team;
  const base = t[task.id];
  if (!base) return { title: task.id, line: '' };
  if (task.id === 'D') {
    if (task.locked) return { title: base.title, line: base.lineLocked };
    return { title: base.title, line: plan === 'premium' || plan === 'enterprise' ? base.linePremium : base.line };
  }
  if (task.id === 'E') {
    return { title: base.title, line: plan && plan !== 'free' ? base.linePaid : base.line };
  }
  return base;
}

function TaskRow({ task, plan, onGo }) {
  const c = onboardingCopy.en.checklist;
  const { title, line } = taskText(task, plan);
  const badge = task.locked ? (c.planBadge[task.required_plan] || 'Standard') : null;
  return (
    <li className="py-2.5 flex gap-3" data-testid={`onboarding-task-${task.id}`} data-done={task.done ? 'true' : 'false'} data-locked={task.locked ? 'true' : 'false'}>
      <span
        aria-hidden="true"
        className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border flex items-center justify-center ${
          task.done
            ? 'bg-[#217922] border-[#217922] dark:bg-[#3BB34A] dark:border-[#3BB34A] text-white'
            : 'border-[#CBD5E0] dark:border-border bg-transparent'
        }`}
      >
        {task.done && <Check className="h-3.5 w-3.5" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${task.done ? 'text-[#4A5568] dark:text-muted-foreground line-through' : task.locked ? 'text-[#4A5568] dark:text-muted-foreground' : 'text-[#0F2D4E] dark:text-white'}`}>
            {title}
          </span>
          {badge && (
            <span className="rounded-full border border-[#E2E8F0] dark:border-border px-2 py-0.5 text-[11px] font-medium text-[#4A5568] dark:text-muted-foreground">
              {badge}
            </span>
          )}
          <span className="sr-only">{task.done ? '(done)' : task.locked ? `(needs the ${badge} plan)` : ''}</span>
        </div>
        {!task.done && (
          <p className="mt-0.5 text-xs text-[#4A5568] dark:text-muted-foreground">
            {line}{' '}
            <button
              type="button"
              onClick={() => onGo(task)}
              className="font-semibold text-[#217922] dark:text-[#3BB34A] hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922] rounded-sm whitespace-nowrap"
              data-testid={`onboarding-task-go-${task.id}`}
            >
              {task.locked ? `${c.seePlans} →` : `${c.go} →`}
            </button>
          </p>
        )}
      </div>
    </li>
  );
}

export default function Checklist() {
  const onb = useOnboarding();
  const c = onboardingCopy.en.checklist;

  // Esc minimises the open panel / sheet.
  useEffect(() => {
    if (!onb.showChecklist || !onb.expanded) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onb.setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onb.showChecklist, onb.expanded, onb]);

  if (!onb.showChecklist) return null;

  const tasks = onb.tasks || [];
  const allSet = onb.all_done;
  const pct = onb.total ? Math.round((onb.doneCount / onb.total) * 100) : 0;

  if (!onb.expanded) {
    return (
      <button
        type="button"
        dir="ltr"
        lang="en"
        onClick={() => onb.setExpanded(true)}
        className={`${panel} fixed z-[45] right-4 sm:right-24 bottom-4 sm:bottom-4 max-sm:bottom-20 px-4 min-h-[48px] sm:min-h-[40px] text-sm font-semibold text-[#0F2D4E] dark:text-white flex items-center gap-2 transition-opacity duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922]`}
        style={fontStyle}
        aria-label={c.open}
        data-testid="onboarding-checklist-pill"
      >
        <span className="h-2 w-2 rounded-full bg-[#217922] dark:bg-[#3BB34A]" aria-hidden="true" />
        {c.pill(onb.doneCount, onb.total)}
      </button>
    );
  }

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-[#0F2D4E] dark:text-white">{c.title}</h2>
          {!allSet && (
            <p className="text-xs text-[#4A5568] dark:text-muted-foreground mt-0.5" data-testid="onboarding-progress">
              {c.progress(onb.doneCount, onb.total)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onb.setExpanded(false)}
          className="rounded-md p-1.5 text-[#4A5568] dark:text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922]"
          aria-label={c.minimise}
          data-testid="onboarding-checklist-minimise"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {allSet ? (
        <p className="mt-3 text-sm text-[#0F2D4E] dark:text-white" data-testid="onboarding-all-set">{c.allSet}</p>
      ) : (
        <>
          <div className="mt-3 h-1.5 w-full rounded-full bg-[#E2E8F0] dark:bg-muted overflow-hidden" role="progressbar" aria-valuemin={0} aria-valuemax={onb.total} aria-valuenow={onb.doneCount}>
            <div className="h-full bg-[#217922] dark:bg-[#3BB34A] transition-[width] duration-150" style={{ width: `${pct}%` }} />
          </div>
          <ul className="mt-2 divide-y divide-[#E2E8F0] dark:divide-border">
            {tasks.map((t) => <TaskRow key={t.id} task={t} plan={onb.plan} onGo={onb.goToTask} />)}
          </ul>
        </>
      )}

      <div className="mt-3 pt-2 border-t border-[#E2E8F0] dark:border-border">
        <button
          type="button"
          onClick={onb.hideChecklist}
          className="text-xs text-[#4A5568] dark:text-muted-foreground hover:underline underline-offset-2 min-h-[32px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#217922] rounded-sm"
          data-testid="onboarding-checklist-hide"
        >
          {c.hide}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop panel */}
      <section
        dir="ltr"
        lang="en"
        aria-label={c.title}
        className={`${panel} hidden sm:block fixed z-[45] right-24 bottom-4 w-[320px] max-h-[70vh] overflow-y-auto p-4 transition-opacity duration-150`}
        style={fontStyle}
        data-testid="onboarding-checklist"
      >
        {body}
      </section>
      {/* Mobile bottom sheet */}
      <div className="sm:hidden fixed inset-0 z-[45]" dir="ltr" lang="en" style={fontStyle}>
        <button
          type="button"
          aria-label={c.minimise}
          className="absolute inset-0 bg-black/40 transition-opacity duration-150"
          onClick={() => onb.setExpanded(false)}
        />
        <section
          aria-label={c.title}
          className={`${panel} absolute inset-x-0 bottom-0 rounded-b-none max-h-[75vh] overflow-y-auto p-4 pb-6`}
          data-testid="onboarding-checklist-sheet"
        >
          {body}
        </section>
      </div>
    </>
  );
}
